import i18n from 'i18n-js';

import { ClosedStatement, EvolvingStatement, match } from './datagram';
import { EvolvingLine } from './line';

export interface Identity {
  /** The hexagram number, in the King Wen order */
  number: number;
}

export interface Guà extends Identity {
  /** The hexagram Mandarin chinese name */
  pictogram: string;
  /** The hexagram name Mandarin romanization */
  name: string;
  /** The localised hexagram name */
  translation: string;
  /** The hexagram lines statement */
  lines: ClosedStatement;
  /** The hexagram leading lines */
  leading?: number[];
  /** The hexagram constituent lines */
  constituent?: number[];
}

export const index = (lines: EvolvingStatement): number | undefined => {
  const found = hexagrams.findIndex((h) => match(lines, h.lines));
  return found !== -1 ? found : undefined;
};

export const from = (lines: EvolvingStatement): number | undefined => {
  const i = index(lines);
  return Number.isSafeInteger(i) ? (i as number) + 1 : undefined;
};

export const mutated = (lines: EvolvingStatement): EvolvingStatement =>
  lines.map((value: EvolvingLine) =>
    // eslint-disable-next-line no-nested-ternary
    value === 6 ? 7 : value === 9 ? 8 : value
  );

export const changing = (
  lines?: EvolvingStatement
): EvolvingStatement | undefined => {
  let any = false;
  let changed;
  if (lines !== undefined) {
    changed = lines.map((value: EvolvingLine) => {
      if (value === 6 || value === 9) {
        any = true;
        return value;
      }
      return undefined;
      // @todo Returns null instead of undefined
    });
  }

  return any ? changed : undefined;
};

export const char = (number: number): string | undefined =>
  number > 0 && number <= 64 ? String.fromCharCode(19903 + number) : undefined;

export const title = (lines: EvolvingStatement): string => {
  const i = index(lines);
  const hexagram = i !== undefined ? hexagrams[i] : undefined;
  return hexagram !== undefined
    ? // @todo i18n dynamically changing locale
      // @todo i18n dynamically changing book translation
      // this function consumer must re-invoke on state (locale or book translation) change
      // @todo char(hexagram.number) in title, not visualized in iOS
      `${hexagram.number}. ${i18n.t(`${hexagram.number}.title`)}`
    : `${i18n.t('lines')} ${JSON.stringify(lines)}`;
};

export const hexagrams: Guà[] = [
  // King Wen order
  {
    number: 1,
    pictogram: '乾',
    name: 'qián',
    translation: 'El creador',
    lines: [7, 7, 7, 7, 7, 7],
    leading: [5]
  },
  {
    number: 2,
    pictogram: '坤',
    name: 'kūn',
    translation: 'Lo receptivo',
    lines: [8, 8, 8, 8, 8, 8],
    leading: [1, 2]
  },
  {
    number: 3,
    pictogram: '屯',
    name: 'zhūn',
    translation: 'La dificultad inicial',
    lines: [7, 8, 8, 8, 7, 8],
    leading: [1, 5]
  },
  {
    number: 4,
    pictogram: '蒙',
    name: 'méng',
    translation: 'La locura juvenil, la ingenuidad',
    lines: [8, 7, 8, 8, 8, 7],
    leading: [2, 5]
  },
  {
    number: 5,
    pictogram: '需',
    name: 'xū',
    translation: 'La espera, la nutrición',
    lines: [7, 7, 7, 8, 7, 8],
    leading: [5]
  },
  {
    number: 6,
    pictogram: '訟',
    name: 'sòng',
    translation: 'El conflicto',
    lines: [8, 7, 8, 7, 7, 7],
    leading: [5]
  },
  {
    number: 7,
    pictogram: '師',
    name: 'shī',
    translation: 'El ejército',
    lines: [8, 7, 8, 8, 8, 8],
    leading: [5]
  },
  {
    number: 8,
    pictogram: '比',
    name: 'bǐ',
    translation: 'La solidaridad, la unión',
    lines: [8, 8, 8, 8, 7, 8],
    leading: [5]
  },
  {
    number: 9,
    pictogram: '小畜',
    name: 'xiǎo chù',
    translation: 'El poder dominante de lo pequeño',
    lines: [7, 7, 7, 8, 7, 7],
    constituent: [4],
    leading: [5]
  },
  {
    number: 10,
    pictogram: '履',
    name: 'lǚ',
    translation: 'La marcha, la conducta',
    lines: [7, 7, 8, 7, 7, 7],
    constituent: [3],
    leading: [5]
  },
  {
    number: 11,
    pictogram: '泰',
    name: 'tài',
    translation: 'La paz',
    lines: [7, 7, 7, 8, 8, 8],
    leading: [2, 5]
  },
  {
    number: 12,
    pictogram: '否',
    name: 'pǐ',
    translation: 'La estagnación, la inmovilidad',
    lines: [8, 8, 8, 7, 7, 7],
    leading: [2, 5]
  },
  {
    number: 13,
    pictogram: '同人',
    name: 'tóng rén',
    translation: 'La comunidad con los hombres, la alianza',
    lines: [7, 8, 7, 7, 7, 7],
    leading: [2, 5]
  },
  {
    number: 14,
    pictogram: '大有',
    name: 'dà yǒu',
    translation: 'La posesión de lo grande, el gran haber',
    lines: [7, 7, 7, 7, 8, 7],
    leading: [5]
  },
  {
    number: 15,
    pictogram: '謙',
    name: 'qiān',
    translation: 'La humildad, la modestia',
    lines: [8, 8, 7, 8, 8, 8],
    leading: [3]
  },
  {
    number: 16,
    pictogram: '豫',
    name: 'yù',
    translation: 'El entusiasmo',
    lines: [8, 8, 8, 7, 8, 8],
    leading: [4]
  },
  {
    number: 17,
    pictogram: '隨',
    name: 'suí',
    translation: 'El séquito, seguir, continuación, conformidad',
    lines: [7, 8, 8, 7, 7, 8],
    leading: [1, 5]
  },
  {
    number: 18,
    pictogram: '蠱',
    name: 'gŭ',
    translation: 'El trabajo sobre lo corrupto, la reparación',
    lines: [8, 7, 7, 8, 8, 7],
    leading: [5]
  },
  {
    number: 19,
    pictogram: '臨',
    name: 'lín',
    translation: 'La aproximación',
    lines: [7, 7, 8, 8, 8, 8],
    leading: [1, 2]
  },
  {
    number: 20,
    pictogram: '觀',
    name: 'guān',
    translation: 'La contemplación, la visión',
    lines: [8, 8, 8, 8, 7, 7],
    leading: [5, 6]
  },
  {
    number: 21,
    pictogram: '噬嗑',
    name: 'shì kè',
    translation: 'Mordiendo a través',
    lines: [7, 8, 8, 7, 8, 7],
    leading: [5]
  },
  {
    number: 22,
    pictogram: '賁',
    name: 'bì',
    translation: 'La gracia',
    lines: [7, 8, 7, 8, 8, 7],
    leading: [2, 6]
  },
  {
    number: 23,
    pictogram: '剝',
    name: 'bō',
    translation: 'La dispersión, el desgaste, dividiendo y apartando',
    lines: [8, 8, 8, 8, 8, 7],
    leading: [6]
  },
  {
    number: 24,
    pictogram: '復',
    name: 'fù',
    translation: 'El retorno',
    lines: [7, 8, 8, 8, 8, 8],
    leading: [1]
  },
  {
    number: 25,
    pictogram: '無妄',
    name: 'wú wàng',
    translation: 'La inocencia, lo inesperado',
    lines: [7, 8, 8, 7, 7, 7],
    leading: [1, 5]
  },
  {
    number: 26,
    pictogram: '大畜',
    name: 'dà chù',
    translation: 'El poder dominante de lo grande',
    lines: [7, 7, 7, 8, 8, 7],
    leading: [5, 6]
  },
  {
    number: 27,
    pictogram: '頤',
    name: 'yí',
    translation:
      'Las comisuras de los labios, la administración de la alimentación',
    lines: [7, 8, 8, 8, 8, 7],
    leading: [5, 6]
  },
  {
    number: 28,
    pictogram: '大過',
    name: 'dà guò',
    translation: 'La preponderancia de lo grande',
    lines: [8, 7, 7, 7, 7, 8],
    leading: [2, 4]
  },
  {
    number: 29,
    pictogram: '坎',
    name: 'kǎn',
    translation: 'Lo insondable, el abismo, el agua',
    lines: [8, 7, 8, 8, 7, 8],
    leading: [2, 5]
  },
  {
    number: 30,
    pictogram: '離',
    name: 'lí',
    translation: 'Lo que se adhiere, lo oscilante, el fuego',
    lines: [7, 8, 7, 7, 8, 7],
    leading: [2, 5]
  },
  {
    number: 31,
    pictogram: '咸',
    name: 'xián',
    translation: 'La influencia, la galantería',
    lines: [8, 8, 7, 7, 7, 8],
    leading: [4, 5]
  },
  {
    number: 32,
    pictogram: '恆',
    name: 'héng',
    translation: 'La duración',
    lines: [8, 7, 7, 7, 8, 8],
    leading: [2]
  },
  {
    number: 33,
    pictogram: '遯',
    name: 'dùn',
    translation: 'La retirada',
    lines: [8, 8, 7, 7, 7, 7],
    leading: [5],
    constituent: [1, 2]
  },
  {
    number: 34,
    pictogram: '大壯',
    name: 'dà zhuàng',
    translation: 'El poder de lo grande',
    lines: [7, 7, 7, 7, 8, 8],
    leading: [4]
  },
  {
    number: 35,
    pictogram: '晉',
    name: 'jìn',
    translation: 'El progreso',
    lines: [8, 8, 8, 7, 8, 7],
    leading: [5]
  },
  {
    number: 36,
    pictogram: '明夷',
    name: 'míng yí',
    translation: 'Oscureciendo la luz',
    lines: [7, 8, 7, 8, 8, 8],
    leading: [2, 5],
    constituent: [6]
  },
  {
    number: 37,
    pictogram: '家人',
    name: 'jiā rén',
    translation: 'La familia, el clan',
    lines: [7, 8, 7, 8, 7, 7],
    leading: [2, 5]
  },
  {
    number: 38,
    pictogram: '睽',
    name: 'kuí',
    translation: 'La oposición',
    lines: [7, 7, 8, 7, 8, 7],
    leading: [2]
  },
  {
    number: 39,
    pictogram: '蹇',
    name: 'jiǎn',
    translation: 'El obstáculo, la obstrucción',
    lines: [8, 8, 7, 8, 7, 8],
    leading: [5]
  },
  {
    number: 40,
    pictogram: '解',
    name: 'xiè',
    translation: 'La liberación',
    lines: [8, 7, 8, 7, 8, 8],
    leading: [2, 5]
  },
  {
    number: 41,
    pictogram: '損',
    name: 'sǔn',
    translation: 'La mengua, la disminución',
    lines: [7, 7, 8, 8, 8, 7],
    leading: [5],
    constituent: [6]
  },
  {
    number: 42,
    pictogram: '益',
    name: 'yì',
    translation: 'El incremento, el aumento',
    lines: [7, 8, 8, 8, 7, 7],
    leading: [2, 5],
    constituent: [1, 4]
  },
  {
    number: 43,
    pictogram: '夬',
    name: 'guài',
    translation: 'La irrupción, pasando a través',
    lines: [7, 7, 7, 7, 7, 8],
    leading: [5],
    constituent: [6]
  },
  {
    number: 44,
    pictogram: '姤',
    name: 'gòu',
    translation: 'Venir al encuentro',
    lines: [8, 7, 7, 7, 7, 7],
    leading: [2, 5],
    constituent: [1]
  },
  {
    number: 45,
    pictogram: '萃',
    name: 'cuì',
    translation: 'La reunión, la recolección, el recogimiento',
    lines: [8, 8, 8, 7, 7, 8],
    leading: [4, 5]
  },
  {
    number: 46,
    pictogram: '升',
    name: 'shēng',
    translation: 'Empujando hacia arriba',
    lines: [8, 7, 7, 8, 8, 8],
    leading: [5],
    constituent: [1]
  },
  {
    number: 47,
    pictogram: '困',
    name: 'kùn',
    translation: 'La opresión, el agotamiento',
    lines: [8, 7, 8, 7, 7, 8]
  },
  {
    number: 48,
    pictogram: '井',
    name: 'jǐng',
    translation: 'El pozo',
    lines: [8, 7, 7, 8, 7, 8]
  },
  {
    number: 49,
    pictogram: '革',
    name: 'gé',
    translation: 'La revolución, el tumulto',
    lines: [7, 8, 7, 7, 7, 8],
    leading: [5]
  },
  {
    number: 50,
    pictogram: '鼎',
    name: 'tǐng',
    translation: 'La caldera',
    lines: [8, 7, 7, 7, 8, 7],
    leading: [5, 6]
  },
  {
    number: 51,
    pictogram: '震',
    name: 'zhèn',
    translation: 'El despertar, el trueno, el combate',
    lines: [7, 8, 8, 7, 8, 8],
    leading: [1]
  },
  {
    number: 52,
    pictogram: '艮',
    name: 'gèn',
    translation: 'Manteniéndose quieto, la montaña',
    lines: [8, 8, 7, 8, 8, 7],
    leading: [6]
  },
  {
    number: 53,
    pictogram: '漸',
    name: 'jiàn',
    translation: 'El desarrollo, el progreso gradual',
    lines: [8, 8, 7, 8, 7, 7],
    leading: [2, 5]
  },
  {
    number: 54,
    pictogram: '歸妹',
    name: 'guī mèi',
    translation: 'La doncella casadera',
    lines: [7, 7, 8, 7, 8, 8],
    leading: [5],
    constituent: [6]
  },
  {
    number: 55,
    pictogram: '豐',
    name: 'fēng',
    translation: 'La abundancia, la plenitud',
    lines: [7, 8, 7, 7, 8, 8],
    leading: [5]
  },
  {
    number: 56,
    pictogram: '旅',
    name: 'lǚ',
    translation: 'El peregrino',
    lines: [8, 8, 7, 7, 8, 7],
    leading: [5]
  },
  {
    number: 57,
    pictogram: '巽',
    name: 'xùn',
    translation: 'Lo dócil, lo penetrante, el viento',
    lines: [8, 7, 7, 8, 7, 7],
    leading: [5],
    constituent: [1, 4]
  },
  {
    number: 58,
    pictogram: '兌',
    name: 'duì',
    translation: 'Lo gozoso, el lago',
    lines: [7, 7, 8, 7, 7, 8],
    leading: [2, 5],
    constituent: [3, 6]
  },
  {
    number: 59,
    pictogram: '渙',
    name: 'huàn',
    translation: 'La dispersión, la disolución',
    lines: [8, 7, 8, 8, 7, 7],
    leading: [5],
    constituent: [2]
  },
  {
    number: 60,
    pictogram: '節',
    name: 'jié',
    translation: 'La limitación',
    lines: [7, 7, 8, 8, 7, 8],
    leading: [5]
  },
  {
    number: 61,
    pictogram: '中孚',
    name: 'zhōng fú',
    translation: 'La propia verdad',
    lines: [7, 7, 8, 8, 7, 7],
    leading: [5],
    constituent: [3, 4]
  },
  {
    number: 62,
    pictogram: '小過',
    name: 'xiǎo guò',
    translation: 'La preponderancia de lo pequeño',
    lines: [8, 8, 7, 7, 8, 8],
    leading: [2, 5]
  },
  {
    number: 63,
    pictogram: '既濟',
    name: 'jì jì',
    translation: 'Después de completar',
    lines: [7, 8, 7, 8, 7, 8],
    leading: [2]
  },
  {
    number: 64,
    pictogram: '未濟',
    name: 'wèi jì',
    translation: 'Antes de completar',
    lines: [8, 7, 8, 7, 8, 7],
    leading: [5]
  }
];
